// When a lurker on a public workspace chooses to join (via "Sign up to join"
// or "Continue with GitHub"), we stash the workspace slug before the OAuth
// round-trip. After they land back authenticated, `useAutoJoinAfterAuth` reads
// it and fires the join automatically — so a fresh signup flows straight into
// the community instead of bouncing them back to the splash's Join button.
//
// sessionStorage (not localStorage): the intent is scoped to this tab's
// sign-up flow and must not leak into a later, unrelated session.

const KEY = "ano:pending-auto-join";
// The OAuth round-trip is quick; expire the intent so an ABANDONED GitHub flow
// (user bailed at WorkOS) can't silently auto-join them on some later, unrelated
// sign-in in the same tab.
const MAX_AGE_MS = 10 * 60_000;

export function setPendingAutoJoin(slug: string): void {
  if (!slug) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ slug, ts: Date.now() }));
  } catch {
    /* private mode / storage disabled — auto-join just won't fire */
  }
}

/**
 * Read AND clear the pending slug (one-shot, so a reload can't re-trigger).
 * Returns null for a missing, malformed, or stale (>10 min) intent.
 */
export function takePendingAutoJoin(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    const parsed = JSON.parse(raw) as { slug?: unknown; ts?: unknown };
    if (typeof parsed.slug !== "string" || typeof parsed.ts !== "number") {
      return null;
    }
    if (Date.now() - parsed.ts > MAX_AGE_MS) return null;
    return parsed.slug || null;
  } catch {
    return null;
  }
}
